/**
 * RTF → plain text for preview and placeholder detection (browser-safe, no Node streams).
 * Uses a small group-aware tokenizer: skips font/color/style tables, ignorable `{\*...}` groups,
 * pictures/objects, and binary `\binN` blobs so the body text is not polluted by header metadata.
 */

const SKIP_DESTINATION_WORDS = new Set([
  'fonttbl',
  'colortbl',
  'stylesheet',
  'filetbl',
  'listtable',
  'listoverridetable',
  'latentstyles',
  'datastore',
  'themedata',
  'colorschememapping',
  'xmlnstbl',
  'generator',
  'info',
  'pgptbl',
  'rsidtbl',
  'rsidroot',
  'mmathpr',
  'footer',
  'footerl',
  'footerr',
  'footerf',
  'header',
  'headerl',
  'headerr',
  'headerf',
  'pict',
  'object',
  'shp',
  'shpgrp',
  'nonshppict',
  'private',
  'background',
  'pnseclvl',
  'pnlvlbody',
  'pnlvlblt',
  'pnlvlcont',
  'tblind',
  'tbllkhdrrows',
  'tbllklastrow',
  'tbllkhdrcols',
  'tbllklastcol',
]);

function skipWs(s, i) {
  let p = i;
  while (p < s.length && /[\s\r\n]/.test(s[p])) p += 1;
  return p;
}

/**
 * @returns {{ word: string, param: string | null, end: number } | null}
 */
function readControlWord(s, i) {
  if (i >= s.length || s[i] !== '\\') return null;
  let p = i + 1;
  if (p >= s.length) return { word: '', param: null, end: p };

  const c0 = s[p];
  // Hex byte \'hh
  if (c0 === "'") {
    if (p + 2 < s.length) {
      const hex = s.slice(p + 1, p + 3);
      if (/^[0-9a-fA-F]{2}$/.test(hex)) {
        return { word: "'", param: hex, end: p + 3 };
      }
    }
    return { word: "'", param: null, end: p + 1 };
  }

  // Unicode \uN?
  if (c0 === 'u' && p + 1 < s.length && /[-\d]/.test(s[p + 1])) {
    let q = p + 1;
    if (s[q] === '-') q += 1;
    while (q < s.length && /\d/.test(s[q])) q += 1;
    const numStr = s.slice(p + 1, q);
    const code = parseInt(numStr, 10);
    let end = q;
    if (s[end] === '?') end += 1;
    else if (s[end] && s[end] !== ' ' && s[end] !== '\\' && s[end] !== '{' && s[end] !== '}') {
      end += 1;
    }
    if (s[end] === ' ') end += 1;
    return { word: 'u', param: String(Number.isFinite(code) ? code : 0), end };
  }

  // \binN — binary run (length in bytes; we approximate with code units for Latin RTF)
  if (c0 === 'b' && s.slice(p, p + 3) === 'bin' && p + 3 < s.length && /\d/.test(s[p + 3])) {
    let q = p + 3;
    while (q < s.length && /\d/.test(s[q])) q += 1;
    const n = parseInt(s.slice(p + 3, q), 10) || 0;
    if (s[q] === ' ') q += 1;
    return { word: 'bin', param: String(n), end: q };
  }

  // Single non-letter control (\~, \_, \-, \\, etc.)
  if (!/[a-zA-Z]/.test(c0)) {
    return { word: c0, param: null, end: p + 1 };
  }

  let q = p;
  while (q < s.length && /[a-zA-Z]/.test(s[q])) q += 1;
  const word = s.slice(p, q).toLowerCase();
  let numStart = q;
  if (s[q] === '-') q += 1;
  while (q < s.length && /\d/.test(s[q])) q += 1;
  const param = q > numStart ? s.slice(numStart, q) : null;
  if (s[q] === ' ') q += 1;
  return { word, param, end: q };
}

/**
 * After `{`, should this group's content be omitted from plain text?
 */
function shouldSkipNewGroup(s, posAfterOpenBrace) {
  let p = skipWs(s, posAfterOpenBrace);
  if (p + 1 < s.length && s[p] === '\\' && s[p + 1] === '*') {
    return true;
  }
  const cw = readControlWord(s, p);
  if (!cw || !cw.word) return false;
  if (SKIP_DESTINATION_WORDS.has(cw.word)) return true;
  return false;
}

function unicodeFromRtfSignedU16(n) {
  let c = parseInt(n, 10);
  if (!Number.isFinite(c)) return '';
  if (c < 0) c += 65536;
  if (c < 0 || c > 0xffff) return '';
  return String.fromCharCode(c);
}

/**
 * @param {string} s
 * @returns {string}
 */
function rtfToPlainTextSync(s) {
  const str = String(s ?? '');
  if (!str.includes('{') && !str.includes('\\')) {
    return str.trim();
  }

  /** @type {boolean[]} */
  const skipStack = [];

  const isSkipping = () => skipStack.some(Boolean);

  let out = '';
  let i = 0;

  while (i < str.length) {
    const ch = str[i];

    if (ch === '{') {
      const parentSkip = isSkipping();
      const localSkip = !parentSkip && shouldSkipNewGroup(str, i + 1);
      skipStack.push(parentSkip || localSkip);
      i += 1;
      continue;
    }

    if (ch === '}') {
      skipStack.pop();
      i += 1;
      continue;
    }

    if (ch === '\\') {
      const cw = readControlWord(str, i);
      if (!cw) {
        i += 1;
        continue;
      }

      if (cw.word === 'bin' && cw.param != null) {
        const n = Math.min(parseInt(cw.param, 10) || 0, 10_000_000);
        i = cw.end + n;
        continue;
      }

      if (isSkipping()) {
        i = cw.end;
        continue;
      }

      if (cw.word === 'par' || cw.word === 'column') {
        out += '\n';
        i = cw.end;
        continue;
      }
      if (cw.word === 'line' || cw.word === 'lbr') {
        out += '\n';
        i = cw.end;
        continue;
      }
      if (cw.word === 'tab') {
        out += '\t';
        i = cw.end;
        continue;
      }
      if (cw.word === "'" && cw.param && /^[0-9a-fA-F]{2}$/.test(cw.param)) {
        const code = parseInt(cw.param, 16);
        out += Number.isFinite(code) && code >= 0 && code <= 255 ? String.fromCharCode(code) : '';
        i = cw.end;
        continue;
      }
      if (cw.word === 'u' && cw.param != null) {
        out += unicodeFromRtfSignedU16(cw.param);
        i = cw.end;
        continue;
      }
      if (cw.word === '\\' || cw.word === '{' || cw.word === '}') {
        out += cw.word;
        i = cw.end;
        continue;
      }

      i = cw.end;
      continue;
    }

    if (isSkipping()) {
      i += 1;
      continue;
    }

    if (ch === '\r' || ch === '\n') {
      i += 1;
      continue;
    }

    out += ch;
    i += 1;
  }

  return out
    .split('\n')
    .map((line) => line.replace(/[ \t\f\v]+/g, ' ').trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * @returns {Promise<string>}
 */
export function rtfStringToPlainText(rtfString) {
  return Promise.resolve(rtfToPlainTextSync(rtfString));
}
